import { normalizeAgentLinkRoute } from '../links/agent-link-validator';
import { OFFERS_REGISTRY } from './offers.registry';
import { ORDERS_REGISTRY } from './orders.registry';
import {
    RegistryDefinition,
    RegistryFilterField,
    RegistryFilterValidationError,
    RegistryFilterValidationResult,
    RegistryId,
} from './registry.types';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ID_RE = /^\d+$/;

/** Каталог поддерживаемых реестров. Для нового реестра - добавить definition сюда. */
export const REGISTRY_CATALOG: Readonly<Record<string, RegistryDefinition>> = {
    [ORDERS_REGISTRY.id]: ORDERS_REGISTRY,
    [OFFERS_REGISTRY.id]: OFFERS_REGISTRY,
};

/** Список реестров в виде `id (title)` для описаний агенту. */
export function listRegistryLabels(): string[] {
    return Object.values(REGISTRY_CATALOG).map((registry) => `${registry.id} (${registry.title})`);
}

/** Получить определение реестра или `null`, если id неизвестен. */
export function getRegistry(id: string): RegistryDefinition | null {
    return REGISTRY_CATALOG[id] ?? null;
}

/**
 * Валидирует filters агента против схемы реестра.
 * Неизвестные/невалидные ключи отбрасываются и попадают в `errors`.
 */
export function validateRegistryParams(
    registryId: string,
    filters: Record<string, unknown> | null | undefined,
): RegistryFilterValidationResult {
    const registry = getRegistry(registryId);

    if (!registry) {
        return {
            validParams: {},
            appliedLabels: [],
            errors: [{ key: 'registry', reason: `unknown registry: ${registryId}` }],
        };
    }

    const fieldByKey = new Map<string, RegistryFilterField>(
        registry.filters.map((field) => [field.key, field]),
    );
    const validParams: Record<string, string> = {};
    const errors: RegistryFilterValidationError[] = [];
    const appliedLabelSet = new Set<string>();
    const input = filters && typeof filters === 'object' && !Array.isArray(filters) ? filters : {};

    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
        errors.push({ key: 'filters', reason: 'filters must be a plain object' });
    }

    for (const [key, rawValue] of Object.entries(input)) {
        const field = fieldByKey.get(key);

        if (!field) {
            errors.push({ key, reason: 'unknown filter key', value: rawValue });
            continue;
        }

        const normalized = normalizeFilterValue(field, rawValue);

        if (normalized === null) {
            errors.push({
                key,
                reason: `invalid value for type ${field.valueType}`,
                value: rawValue,
            });
            continue;
        }

        validParams[key] = normalized;
        appliedLabelSet.add(field.label);
    }

    const appliedLabels = registry.filters
        .map((field) => field.label)
        .filter((label, index, all) => appliedLabelSet.has(label) && all.indexOf(label) === index);

    return { validParams, appliedLabels, errors };
}

/**
 * Собирает URL реестра с валидными query-параметрами.
 * Возвращает `null`, если реестр неизвестен.
 */
export function buildRegistryUrl(
    registryId: string,
    validParams: Record<string, string>,
): string | null {
    const registry = getRegistry(registryId);

    if (!registry) {
        return null;
    }

    const base = normalizeAgentLinkRoute(registry.path);

    if (!base) {
        return null;
    }

    const query = new URLSearchParams(validParams).toString();

    if (!query) {
        return base;
    }

    const separator = registry.queryMode === 'hash' ? '#?' : '?';

    return `${base}${separator}${query}`;
}

function normalizeFilterValue(field: RegistryFilterField, rawValue: unknown): string | null {
    if (rawValue === null || rawValue === undefined) {
        return null;
    }

    switch (field.valueType) {
        case 'boolean': {
            if (typeof rawValue === 'boolean') {
                return rawValue ? 'true' : 'false';
            }

            if (typeof rawValue === 'string') {
                const lower = rawValue.trim().toLowerCase();

                if (lower === 'true' || lower === 'false') {
                    return lower;
                }
            }

            return null;
        }

        case 'enum': {
            const asString = String(rawValue).trim();

            if (!field.enumValues?.includes(asString)) {
                return null;
            }

            return asString;
        }

        case 'date': {
            const asString = String(rawValue).trim();

            return DATE_RE.test(asString) ? asString : null;
        }

        case 'id': {
            if (typeof rawValue === 'number' && Number.isFinite(rawValue) && rawValue >= 0) {
                return String(Math.trunc(rawValue));
            }

            const asString = String(rawValue).trim();

            return ID_RE.test(asString) ? asString : null;
        }

        case 'string': {
            const asString = String(rawValue).trim();

            return asString.length > 0 ? asString : null;
        }

        default:
            return null;
    }
}
